// Mapa ruta → módulo del menú (menuKey sin el prefijo `menu:`).
// Lo usa ProtectedRoute para saber a qué módulo pertenece la ruta actual y
// aplicar el bloqueo (estado "Bloqueado" de roles personalizados) por URL.
//
// Se evalúa por prefijo más largo primero, así `/quotes/new` gana sobre `/quotes`.

const ROUTE_MENU_MAP: Array<[string, string]> = [
  ['/dashboard', 'dashboard'],
  // 18-oct: el Dashboard de cotizaciones se mudó a Cotiza › Visión general.
  ['/cotiza', 'dashboard'],
  ['/master-data', 'master-data'],
  ['/pricing-models', 'pricing'],
  // `/pricing-rules` NO va acá: desde el 26-sep se guía por su permiso, no por
  // `menu:pricing` (ver ROUTE_PERMISSION_MAP, abajo). Y `/pricing` no la atrapa:
  // `startsWith('/pricing/')` no calza con `/pricing-rules`.
  ['/pricing', 'pricing'],
  /*
    L7 (vault 279): Precios Dinámicos no estaba en el mapa. Igual que el Asistente
    Arancelario (abajo): el menú lo escondía, pero escribiendo la dirección entraba
    cualquiera con sesión. La carga manual y «Revisar vínculos» son del mismo módulo.
  */
  ['/dynamic-pricing', 'pricing'],
  ['/identificacion', 'pricing'],
  ['/revisar-vinculos', 'pricing'],
  // Compite: las vistas nuevas de Precios Dinámicos, mismo módulo.
  ['/compite/monitoreo', 'pricing'],
  ['/compite/productos', 'pricing'],
  ['/compite/competencia', 'pricing'],
  ['/compite/analitica', 'pricing'],
  // Ronda E (T16-2): las tiendas conectadas y su historial (el servidor, igual).
  ['/tiendas-conectadas', 'pricing'],
  ['/historial-push', 'pricing'],
  ['/quotes/new', 'nueva-cotizacion'],
  ['/quotes', 'quotes'],
  ['/customers', 'customers'],
  ['/approvals', 'approvals'],
  // Trade Intelligence. El orden acá no importa (se ordena por longitud abajo),
  // pero sí que `/trade/cotizador` no se confunda con `/trade/cotizaciones`:
  // cotizar y ver cotizaciones son permisos distintos.
  // La portada de Comercia pide el módulo, como su grupo del menú.
  ['/comercia', 'import-tracking'],
  ['/trade/cotizador', 'trade-cotizador'],
  ['/trade/tarifario', 'trade-tarifario'],
  ['/trade/aduana', 'trade-aduana'],
  ['/trade/cotizaciones', 'trade-ordenes'],
  ['/trade/embarques', 'trade-ordenes'],
  ['/trade/contenedores', 'trade-ordenes'],
  ['/trade/indicadores', 'trade-ordenes'],
  ['/trade', 'trade-cotizador'],
  ['/importaciones', 'trade-ordenes'],

  /*
    El Asistente Arancelario no estaba en el mapa.

    🔴 Sin entrada acá, `getMenuKeyForPath` devuelve `undefined` y
    `ProtectedRoute` no bloquea nada: bastaba estar logueado para abrir
    `/tariff-assistant` escribiendo la URL, aunque el módulo estuviera
    apagado para ese rol. El menú lo escondía; la ruta no.
  */
  ['/tariff-assistant', 'trade-aduana'],

  /*
    Los catálogos del flete se controlan con el permiso del TARIFARIO.

    Sus páginas viven bajo `/master-data/…`, así que el prefijo genérico las
    ataba a `master-data`. Desde que el menú los mueve a Datos Maestros pero
    conservando `menuKey: 'trade-tarifario'`, sin estas entradas quedaba un
    desfase feo: la entrada se veía en el menú y al hacer clic aparecía
    "módulo bloqueado". Ganan por ser el prefijo más largo.
  */
  ['/master-data/freight-providers', 'trade-tarifario'],
  ['/master-data/freight-locations', 'trade-tarifario'],
  ['/master-data/freight-services', 'trade-tarifario'],
  ['/master-data/freight-leg-catalog', 'trade-tarifario'],
  ['/import-tracking', 'import-tracking'],
  ['/data-import', 'data-import'],
  /*
    Estrategias de Mercado responde al candado `data-import` (el `menuKey` del
    menú). Sin esta entrada, cualquiera logueado la abría por URL aunque el
    módulo estuviera bloqueado para su rol: el mismo agujero de /tariff-assistant
    y de /pricing-rules. Desde el plan 3 (épica H) Reglas vive adentro: esta
    entrada cuida la página y las pestañas Reglas y Productos piden, además, el
    permiso de ver reglas (4B T3).
  */
  ['/precio-inteligente', 'data-import'],
  /*
    Compite (rediseño del 14-oct): Estrategias de Mercado se partió en dos
    pantallas y cada una hereda el mismo candado. Sin estas entradas,
    `getMenuKeyForPath` devuelve undefined y se entraría por URL con el módulo
    bloqueado: el agujero que ya pasó tres veces (ver arriba).
    `/compite/decisiones` cubre también `/compite/decisiones/revisar`.
  */
  ['/compite/decisiones', 'data-import'],
  ['/compite/estrategia', 'data-import'],
  ['/analytics', 'analytics'],
  ['/ai-intelligence', 'ai-intelligence'],
  ['/contracts', 'contracts'],
  /*
    🔴 Oportunidades no estaba en el mapa. Sin entrada acá,
    `getMenuKeyForPath` devuelve undefined y no se bloquea nada: el modulo podia
    estar apagado para ese rol, la entrada no aparecia en el menu, y aun asi se
    entraba escribiendo /opportunities. Es el mismo bug que ya habia pasado con
    el Asistente Arancelario, unas lineas mas arriba.
  */
  ['/opportunities', 'opportunities'],
  ['/pricer-datec', 'pricer-datec'],
  ['/pricer-solar', 'pricer-solar'],
  ['/integrations', 'integrations'],
  /*
    Roles y permisos tiene ruta propia desde que salió de Configuración. Va al
    módulo `admin`, el mismo que /users y /settings: es administración de la
    empresa. Sin esta entrada, `getMenuKeyForPath` devuelve undefined y la ruta
    no se bloquea por URL — el mismo bug que ya pasó dos veces (Asistente
    Arancelario y Oportunidades, más arriba).
  */
  ['/permisos', 'admin'],
  /*
    Mi empresa (Usuarios, Roles, Equipos, Aprobaciones, Documentos, Pagos) es la
    administración de la empresa: mismo módulo `admin` que el enlace del menú
    (sidebar.tsx). Sin esta entrada `getMenuKeyForPath` devolvía undefined y, si
    el módulo estaba bloqueado, la pantalla se abría igual escribiendo la
    dirección — el mismo bug de siempre.
  */
  ['/mi-empresa', 'admin'],
  ['/admin', 'admin'],
  /*
    El panel se mudó de /admin a /plataforma (feat/panel-de-plataforma).
    Mismo módulo, misma llave: la mudanza cambia de dónde cuelga la pantalla,
    no a qué permiso responde. `/admin` queda arriba porque las direcciones
    viejas siguen redirigiendo y su ruta real sigue montada mientras existe.
  */
  ['/plataforma', 'admin'],
  ['/tutorial', 'tutorial'],
];

// Ordenado por longitud de prefijo desc para que el match más específico gane.
const SORTED = [...ROUTE_MENU_MAP].sort((a, b) => b[0].length - a[0].length);

/** Devuelve el menuKey del módulo al que pertenece un pathname, o undefined. */
export function getMenuKeyForPath(pathname: string): string | undefined {
  const match = SORTED.find(([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`));
  return match?.[1];
}

/*
  Rutas que se guían por un PERMISO y no por un módulo del menú.

  Reglas tenía la llave `menu:pricing`, compartida con Modelos, Precios
  Dinámicos y Descuentos. Desde el 26-sep (plan 4B-datos T1b) tiene cinco
  permisos propios; entrar pide ver reglas (el backend lo da también a quien
  tiene uno de escritura). `modulo` es el módulo que la empresa tiene que haber
  comprado: sirve para que el cartel diga de quién es la puerta cerrada.
*/
const ROUTE_PERMISSION_MAP: Array<[string, { permiso: string; modulo: string }]> = [
  ['/pricing-rules', { permiso: 'view_pricing_rules', modulo: 'pricing' }],
];

/** El permiso que pide un pathname, o undefined si se guía sólo por el menú. */
export function getPermissionForPath(
  pathname: string
): { permiso: string; modulo: string } | undefined {
  const match = ROUTE_PERMISSION_MAP.find(
    ([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
  return match?.[1];
}

/*
  P3-D10 (27-sep): rutas que también abren con ver reglas, aunque falte su
  módulo del menú. Estrategias de Mercado lleva Reglas adentro desde el plan 3
  (Tarea 1): quien ve reglas y no tiene `menu:data-import` entra y la página le
  muestra sólo Reglas y Productos. Sin esto, `/pricing-rules` lo redirigía a un
  cartel de módulo cerrado.
*/
// Compite › Estrategia lleva Reglas y Productos adentro (rediseño del 14-oct):
// abre con ver reglas igual que la página de antes. Decisiones no: sin el
// módulo no hay propuestas que decidir, y su página manda a Reglas.
const RUTAS_QUE_ABREN_CON_VER_REGLAS = ['/precio-inteligente', '/compite/estrategia'];

export function abreConVerReglas(pathname: string): boolean {
  return RUTAS_QUE_ABREN_CON_VER_REGLAS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/*
  2.7b (Fase 2): el registro se cierra por defecto.

  Toda ruta que cuelga de `ProtectedRoute` tiene que figurar en UNO de estos tres
  lugares: el mapa de módulos (`ROUTE_MENU_MAP`), el de permisos
  (`ROUTE_PERMISSION_MAP`) o esta lista de rutas LIBRES, cada una con su motivo.
  Si no figura en ninguno y el router no le pasó un `menuKey` explícito,
  `ProtectedRoute` la cierra: antes la dejaba pasar (`getMenuKeyForPath` devolvía
  undefined y nadie se enteraba). Un test (`route-modules.test.ts`) compara el
  router contra este registro y falla si falta o sobra una ruta.

  «Libre» NO quiere decir «abierta a cualquiera»: quiere decir que la ruta no se
  guía por un módulo del menú. Muchas igual piden un rol o un permiso adentro
  (`RequireRole`); el motivo lo dice. Es exactamente lo que ya pasaba hoy: este
  registro sólo lo vuelve explícito.
*/
const RUTAS_LIBRES: Array<[string, string]> = [
  [
    '/materials',
    'Materiales: la abren Cotiza, Comercia («Ver en Materiales») y Datos Maestros, y vendedores y aprobadores de fábrica no tienen master-data. El servidor la deja a toda empresa con sesión.',
  ],
  ['/help', 'Ayuda: la ve cualquier persona con sesión, no es un módulo comprable.'],
  ['/profile', 'Mi perfil: es de la propia persona, no de un módulo.'],
  [
    '/configuracion/equipos',
    'Ficha de un equipo: la pantalla pide ser administrador de la empresa (RequireRole).',
  ],
  ['/project-status', 'Estado del proyecto: pantalla informativa, sin módulo propio hoy.'],
  [
    '/settings',
    'Configuración de la empresa: pide ser administrador (RequireRole). Hallazgo: sin módulo en el mapa, igual que antes.',
  ],
  [
    '/developer',
    'Documentación de la API: pide ser administrador (RequireRole). Sin módulo propio hoy.',
  ],
  [
    '/claves-api',
    'Claves de API: pide ser administrador (RequireRole). El servidor las controla aparte (/api/keys).',
  ],
  ['/integracion-api', 'Guía de integración por API: pide ser administrador (RequireRole).'],
  [
    '/ai-productivity',
    'Productividad con IA: sin módulo propio hoy. Hallazgo: decidir si va con ai-intelligence.',
  ],
];

function coincide(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/** Las rutas libres con su motivo (para el guardián y para quien lea el registro). */
export function rutasLibres(): ReadonlyArray<{ prefijo: string; motivo: string }> {
  return RUTAS_LIBRES.map(([prefijo, motivo]) => ({ prefijo, motivo }));
}

/** La ruta figura como libre (con motivo) en el registro. */
export function esRutaLibre(pathname: string): boolean {
  return RUTAS_LIBRES.some(([prefix]) => coincide(pathname, prefix));
}

/** Los prefijos de módulo del registro (para el guardián). */
export function prefijosDeModulo(): string[] {
  return ROUTE_MENU_MAP.map(([prefix]) => prefix);
}

/** Los prefijos de permiso del registro (para el guardián). */
export function prefijosDePermiso(): string[] {
  return ROUTE_PERMISSION_MAP.map(([prefix]) => prefix);
}

/**
 * ¿Declaró esta ruta un módulo, un permiso o `libre`? Es la pregunta de «cerrado
 * por defecto»: si la respuesta es no, `ProtectedRoute` no la deja pasar.
 */
export function estaEnElRegistro(pathname: string): boolean {
  return (
    getMenuKeyForPath(pathname) !== undefined ||
    getPermissionForPath(pathname) !== undefined ||
    esRutaLibre(pathname)
  );
}
